import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { NgRedux, select } from '@angular-redux/store';
import { UntypedFormGroup, UntypedFormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { EmergencyContact } from './models/emergencyContact.model';
import { TranslationService } from '../../../common/services/languages/translation.service';
import { AppState } from '../../../store/state.model';
import { SpinnerRef } from '../../../common/components/spinner/overlay-ref.service';
import { CustomerDispatcher } from '../../../store/customer/customer-dispatcher';
import { OrchestrationService } from '../../services/orchestration-service/orchestration.service';
import { SpinnerService } from '../../../common/components/spinner/overlay-spinner.service';
import { SnackBarService } from '../../../common/services/snack-bar/snack-bar.service';
import { Observable, Subscription } from 'rxjs';
import { ApiResponseState } from '../../../store/api-response/api-response-state.model';
import { ApiResponseActions } from '../../../store/api-response/api-response-actions';
import { SidenavService } from '../../services/sidenav.service';
import { NgIf } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatError } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-emergency-contact',
    templateUrl: './emergency-contact.component.html',
    styleUrls: ['./emergency-contact.component.scss'],
    imports: [NgIf, MatButton, FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatError, TranslatePipe]
})
export class EmergencyContactComponent implements OnInit, OnDestroy {
  /**
  * @property Will contain vehicle details payload
  */
  @Input() vehicleDetails: object;
  /**
 *@var Declaration of spinner Ref
 **/
  public spinnerRef: SpinnerRef;
  /**
  *@property customer to store the form of email or phone
  */
  public emergencyContact: UntypedFormGroup;
  /**
  *@property model to store the model (Email)
  **/
  public model: any;
  /**
  *@property buttonAction to store the translation for emergency contact button
  **/
  public buttonAction: string;
  /**
  *@property isContactExist to store the flag as true if emergency contact exists
  **/
  public isContactExist: boolean;
  /**
   * @property Property to store the edit mode
   */
  public editMode: boolean;
  /**
   * @property Property to store if the user is admin
   */
  public isAdmin: boolean;
  /**
   * @property Property to store the api subscription
   */
  public apiSubscription: Subscription;
  /**
   *@property apiObservable Property to observe the redux customer store
  **/
  @select('api') readonly apiObservable: Observable<ApiResponseState>;
  /**
   * @property to store emergencyContactDetails
   */
  public emergencyContactDetails: object;
  constructor(
    public translate: TranslationService,
    public reduxStore: NgRedux<AppState>,
    public customerDispatcher: CustomerDispatcher,
    public orch: OrchestrationService,
    public fb: UntypedFormBuilder,
    public spinner: SpinnerService,
    private snackBarService: SnackBarService,
    private sidenav: SidenavService,
    
  ) {
  }
  ngOnInit() {
    this.model = new EmergencyContact();
    this.emergencyContact = this.fb.group(this.model.form);
    this.isContactExist = false;
    this.buttonAction = '';
    if (this.vehicleDetails['emergencyContact']) { 
      this.emergencyContactDetails = this.vehicleDetails['emergencyContact']
      this.getCustomerData([this.emergencyContactDetails])
    }

    this.apiSubscription = this.apiObservable.subscribe((api) => {
      if (api.apiAction === ApiResponseActions.SET_UPDATE_EMERGENCY_CONTACT && api.updateEmergencyContact['type'] === 'success') {
        this.toggleEditMode();
        this.snackBarService.showToast(this.translate.getCustomTranslation('update-account.labels.updated-successfully')['value']);
        this.orch.getEmergencyContact(this.vehicleDetails);
      }
      if (api.apiAction === ApiResponseActions.SET_EMERGENCY_CONTACT && api.updateEmergencyContact) {
        const tempVcmHistory = api.apiAction === ApiResponseActions.SET_EMERGENCY_CONTACT && api.emergencyContact['type'] === 'success' && !!api.emergencyContact['type'] ?
          this.getCustomerData(api.emergencyContact['payload']) : null;
      } else if (api.updateEmergencyContact['type'] === 'error' && api.updateEmergencyContact['errorMessage'] == 'Subscription not found') {
          this.snackBarService.showToast(this.translate.getCustomTranslation('update-account.labels.no-subscription')['value']);
        }
    });
  }
  /**
   * @function getCustomerData Get the customer info from redux
   **/
  public getCustomerData(emergencyContact: any): void {
    this.isContactExist = false;
    this.buttonAction = this.model.actionName[0];
    this.emergencyContactDetails = emergencyContact[emergencyContact.length - 1];
    this.setEmergencyContactForm();
  }
  /**
   * @function setForm will disable the form when called
   * @memberof EmergencyContactComponent
   */
  public setForm() {
    this.isContactExist = true;
    this.buttonAction = this.model.actionName[1];
  }
  /**
   * @function setForm will disable the form when called
   * @memberof EmergencyContactComponent
   */
  public setEmergencyContactForm() {
    if (!!this.emergencyContactDetails) { this.setForm(); }
    this.emergencyContact.setValue({
      firstName: !!this.emergencyContactDetails ? this.emergencyContactDetails['firstName'] : '',
      lastName: !!this.emergencyContactDetails ? this.emergencyContactDetails['lastName'] : '',
      phoneNumber: !!this.emergencyContactDetails ? this.emergencyContactDetails['phoneNumber'] : '',
      id: !!this.emergencyContactDetails && !!this.emergencyContactDetails['id'] ? this.emergencyContactDetails['id'] : '',
    });
  }
  /**
   * on destroy method
   */
  ngOnDestroy() {
    this.apiSubscription.unsubscribe();
  }
  /**
   * @function updateEmergencyContact updates the emergency contact information
   */
  public updateEmergencyContact() {
    const emergencyContact = this.formatEmergencyContact();
    this.orch.updateEmergencyContact({ emergencyContact, vin: this.vehicleDetails['vin'], subscriberGuid: this.vehicleDetails['subscriberGuid'] }, this.vehicleDetails);
  }

  /**
   * @function formatEmergencyContact Gets the formatted emergency contact
   */
  public formatEmergencyContact() {
    const { firstName, lastName, phoneNumber, id } = this.emergencyContact.value;
    return {
      firstName,
      lastName,
      phoneNumber,
      id
    };
  }

  /**
   * @method toggleEditMode sets the edit mode and restores the data if canceled
   */
  public toggleEditMode() {
    if (this.editMode) {
      this.disableFormFields();
      this.setEmergencyContactForm();
    } else {
      this.enableFormFields();
    }
    this.editMode = !this.editMode;
  }

  /**
   * @method disableFormFields disables form fields
   */
  public disableFormFields() {
    this.emergencyContact.controls.firstName.disable();
    this.emergencyContact.controls.lastName.disable();
    this.emergencyContact.controls.phoneNumber.disable();
    this.emergencyContact.controls.id.disable();
  }

  /**
   * @method enableFormFields enables form fields
   */
  public enableFormFields() {
    this.emergencyContact.controls.firstName.enable();
    this.emergencyContact.controls.lastName.enable();
    this.emergencyContact.controls.phoneNumber.enable();
    this.emergencyContact.controls.id.enable();
  }

  
  /**
  * @function close closes the side nav
  **/
  public close(): void {
    if (this.sidenav && this.sidenav.close) {
      this.sidenav.close();
    }
  }
}
